import type { LDClient, LDContext } from "launchdarkly-js-client-sdk";
import * as LD from "launchdarkly-js-client-sdk";
import {
  ALPHA_FLAG_KEY,
  getLaunchDarklyClientSideId,
  isAlphaAccessGatingEnabled,
} from "./launchDarklyConfig";

let client: LDClient | null = null;
let initPromise: Promise<LDClient | null> | null = null;

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

async function getClient(): Promise<LDClient | null> {
  if (!isAlphaAccessGatingEnabled()) return null;

  const clientSideId = getLaunchDarklyClientSideId();
  if (!clientSideId) return null;

  if (client) return client;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const instance = LD.initialize(clientSideId, {
      kind: "user",
      key: "anonymous",
      anonymous: true,
    });
    try {
      await instance.waitForInitialization(10);
    } catch {
      // Offline or LD outage — fail closed in production gating mode.
      return null;
    }
    client = instance;
    return instance;
  })();

  return initPromise;
}

/** Warm the LD client at boot (production only). Safe no-op elsewhere. */
export async function initLaunchDarkly(): Promise<void> {
  await getClient();
}

function contextForEmail(email: string): LDContext {
  const key = normalizeEmail(email);
  return {
    kind: "user",
    key,
    email: key,
  };
}

/**
 * Returns whether the email is allowlisted for alpha.
 * When gating is off, always true. When gating is on but LD is unavailable, false.
 */
export async function hasAlphaAccess(
  email: string | undefined
): Promise<boolean> {
  if (!isAlphaAccessGatingEnabled()) return true;
  const normalized = email ? normalizeEmail(email) : "";
  if (!normalized || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
    return false;
  }

  const ld = await getClient();
  if (!ld) return false;

  try {
    // variation() still reflects the *previous* context until identify completes;
    // use the flag set returned by identify (LD SDK docs).
    const flags = await ld.identify(contextForEmail(normalized));
    const value = flags[ALPHA_FLAG_KEY];
    return value === true;
  } catch {
    return false;
  }
}

export async function closeLaunchDarkly(): Promise<void> {
  if (client) {
    client.close();
    client = null;
  }
  initPromise = null;
}
